import { useMutation, useQuery } from '@tanstack/react-query'

import { errorBoundary } from '@/services/errorBoundary'
import { RemainingVotesData, useRemainingVotes } from '@/services/votes'
import { queryClient } from '@/lib/queryClient'

import { getVoteStatus, IVoteStatusDTO, vote, VoteType } from './api'

export enum VotingStatus {
  Loading = 'LOADING',
  Open = 'OPEN',
  Closed = 'CLOSED',
  Voted = 'VOTED',
  NoVotes = 'NO_VOTES',
  Owner = 'OWNER',
  Error = 'ERROR',
}

export const useVoteButton = ({ contestantId, isActiveSlide }) => {
  const { votes } = useRemainingVotes()

  const {
    data: voteStatus,
    isLoading: queryLoading,
    isError: queryError,
  } = useQuery({
    queryKey: ['voteStatus', contestantId],
    queryFn: () => getVoteStatus(contestantId),
    enabled: isActiveSlide,
  })

  const {
    mutate,
    data: voteResult,
    isPending: mutationLoading,
    error,
    isError: mutationError,
  } = useMutation({
    mutationKey: ['vote', contestantId],
    mutationFn: vote,
    onError: errorBoundary,
    onSuccess: (data) => {
      if (data?.voter?.hasVotedOnce) {
        queryClient.setQueryData(['hasVotedOnce'], data?.voter?.hasVotedOnce)
      }
      queryClient.setQueryData(['voteStatus', contestantId], data)
      queryClient.setQueryData(
        ['remainingVotes'],
        (prev: RemainingVotesData) => ({
          ...prev,
          remainVotes: data.voter.remainVotes,
        })
      )
    },
  })

  const status = voteResult || voteStatus
  const isLoading = queryLoading || mutationLoading
  const isError = queryError || mutationError

  return {
    status: isError ? VotingStatus.Error : getButtonStatus(isLoading, status),
    voteStatus: status?.vote,
    vote(voteType: VoteType) {
      mutate({
        contestantId,
        voteType,
      })
    },
    areVotesAvailable: votes > 0,
    error,
    isError,
  }
}

function getButtonStatus(isLoading: boolean, status?: IVoteStatusDTO) {
  if (isLoading) {
    return VotingStatus.Loading
  }

  if (status?.vote) {
    return VotingStatus.Voted
  }

  if (status && !status.voter.remainVotes) {
    return VotingStatus.NoVotes
  }

  if (status && status.voter.id === status.contestant.userId) {
    return VotingStatus.Owner
  }

  return VotingStatus.Open
}
